import { useMemo, useEffect, useState, useRef } from "react";
import { twMerge } from "tailwind-merge";
import { z, ZodTypeAny } from "zod";
import { AnimatePresence, motion } from "motion/react";
import { useForm } from "@shared/features/form/useForm";
import { Form, FieldType } from "@shared/features/form/Form";
import { QuizQuestionSet as QuizQuestionSetType } from "@platform/types/quiz.types";
import { makeQuestionSetFormFields } from "./makeQuestionSetFormFields";
import { buildFormSchemaFromFields } from "./buildFormSchemaFromFields";
import { filterFieldsByQuestionLogic } from "./filterFieldsByQuestionLogic";
import { QuizActions } from "./QuizActions";

type Props = {
  questionSet: QuizQuestionSetType;
  onSubmit: (data: z.infer<ZodTypeAny>) => Promise<void>;
  onBack: () => void;
  defaultValues: Record<string, any>;
  filterQuestionSet?: (
    question: QuizQuestionSetType["questions"][number],
    index: number
  ) => boolean;
  isSubmitting: boolean;
  stepIndex: number;
  totalSteps: number;
  buttonLabel: string;
};

export function QuizQuestionSet({
  questionSet,
  buttonLabel,
  onSubmit,
  onBack,
  defaultValues,
  filterQuestionSet = () => true,
  stepIndex,
  totalSteps,
  isSubmitting: isSubmittingProp = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [animationDirection, setAnimationDirection] = useState(1);

  const allFields = useMemo(
    () =>
      makeQuestionSetFormFields(
        questionSet.questions.filter(filterQuestionSet)
      ),
    [questionSet, filterQuestionSet]
  );

  const schema = useMemo(
    () => buildFormSchemaFromFields(allFields),
    [allFields]
  );

  const { form, handleSubmit } = useForm({
    defaultValues,
    schema,
    onSubmit,
  });

  useEffect(() => {
    if (Object.keys(defaultValues).length > 0) {
      form.reset(defaultValues);
    }
  }, [defaultValues]);

  const values = form.watch();

  const visibleFields = useMemo(() => {
    return filterFieldsByQuestionLogic(allFields, values);
  }, [allFields, values]);

  const { isValid, isSubmitting, isLoading } = form.formState;

  const submitAndUpdateAnimationDirection = async () => {
    await handleSubmit();
    setAnimationDirection(1);
  };

  const handleBack = () => {
    setAnimationDirection(-1);
    onBack();
  };

  return (
    <div className="flex-1 flex flex-col justify-between max-h-screen overflow-y-auto">
      <AnimatePresence mode="wait" custom={animationDirection}>
        <motion.div
          key={stepIndex}
          ref={containerRef}
          initial={{ x: animationDirection * 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          style={{ width: "100%" }}
          exit={{ x: animationDirection * -100, opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
          className="flex-1 flex flex-col justify-between"
        >
          <Form
            key={stepIndex}
            form={form}
            fields={visibleFields as FieldType[]}
            className={twMerge(
              "flex-1 flex flex-col justify-between w-full bg-brand-50 text-brand-800"
            )}
            innerClassName={twMerge("w-full mx-auto gap-12 p-8 justify-center")}
            isSubmitting={isSubmittingProp}
            formFieldClassName="gap-2"
            errorContainerClassName="px-8 max-w-xl mx-auto mb-8"
          />
        </motion.div>
      </AnimatePresence>
      <QuizActions
        onBack={handleBack}
        onSubmit={submitAndUpdateAnimationDirection}
        label={buttonLabel}
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        disabled={!isValid || isSubmitting || isLoading || isSubmittingProp}
      />
    </div>
  );
}

export default QuizQuestionSet;
